import React, { useState, useEffect } from 'react';
import { apiClient, useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Settings as SettingsIcon, 
  Building,
  Users,
  MessageSquare,
  Save,
  Plus,
  Trash,
  Edit,
  Upload,
  Image as ImageIcon,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const SettingsPage = () => {
  const [settings, setSettings] = useState({
    clinic_name: '',
    clinic_address: '',
    clinic_phone: '',
    clinic_logo: null,
    edit_lock_hours: 24
  });
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [showUserForm, setShowUserForm] = useState(false);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const { user: currentUser } = useAuth();

  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'nurse'
  });

  const [templateForm, setTemplateForm] = useState({
    name: '',
    subject: '',
    body: '',
    template_type: 'follow_up'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, usersRes, templatesRes] = await Promise.all([
        apiClient.get('/settings'),
        apiClient.get('/users'),
        apiClient.get('/message-templates')
      ]);
      setSettings(settingsRes.data);
      setUsers(usersRes.data);
      setTemplates(templatesRes.data);
    } catch (error) {
      console.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      // Don't include logo in regular settings save - it's handled separately
      const { clinic_logo, ...settingsWithoutLogo } = settings;
      await apiClient.put('/settings', settingsWithoutLogo);
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a PNG, JPG, or WEBP image');
      return;
    }

    // Validate file size (500KB)
    if (file.size > 500 * 1024) {
      toast.error('Logo must be under 500KB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await apiClient.post('/settings/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setSettings({ ...settings, clinic_logo: response.data.logo });
      toast.success('Logo uploaded successfully');
      // Reload the page to update the header
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await apiClient.delete('/settings/logo');
      setSettings({ ...settings, clinic_logo: null });
      toast.success('Logo removed');
      // Reload the page to update the header
      window.location.reload();
    } catch (error) {
      toast.error('Failed to remove logo');
    }
  };

  const handleCreateUser = async () => {
    if (!userForm.email || !userForm.password || !userForm.full_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await apiClient.post('/auth/register', userForm);
      toast.success('Staff account created');
      setShowUserForm(false);
      setUserForm({ email: '', password: '', full_name: '', role: 'nurse' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleDeleteUser = async () => {
    try {
      await apiClient.delete(`/users/${deleteUserId}`);
      toast.success('User deleted');
      setDeleteUserId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name || !templateForm.subject || !templateForm.body) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      if (editingTemplate) {
        await apiClient.put(`/message-templates/${editingTemplate.template_id}`, templateForm);
        toast.success('Template updated');
      } else {
        await apiClient.post('/message-templates', templateForm);
        toast.success('Template created');
      }
      setShowTemplateForm(false);
      setEditingTemplate(null);
      setTemplateForm({ name: '', subject: '', body: '', template_type: 'follow_up' });
      fetchData();
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await apiClient.delete(`/message-templates/${templateId}`);
      toast.success('Template deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'doctor': return 'bg-primary/10 text-primary';
      case 'nurse': return 'bg-blue-100 text-blue-700';
      case 'cashier': return 'bg-amber-100 text-amber-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl font-bold flex items-center gap-3">
          <SettingsIcon className="w-6 h-6 text-primary" strokeWidth={1.5} />
          Settings
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Manage your clinic settings
        </p>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="clinic">
        <TabsList>
          <TabsTrigger value="clinic">
            <Building className="w-4 h-4 mr-2" />
            Clinic
          </TabsTrigger>
          <TabsTrigger value="staff">
            <Users className="w-4 h-4 mr-2" />
            Staff
          </TabsTrigger>
          <TabsTrigger value="templates">
            <MessageSquare className="w-4 h-4 mr-2" />
            Messages
          </TabsTrigger>
        </TabsList>

        {/* Clinic Settings Tab */}
        <TabsContent value="clinic" className="mt-4 space-y-4">
          {/* Logo Upload Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ImageIcon className="w-5 h-5" />
                Clinic Logo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-6">
                {/* Logo Preview */}
                <div className="flex-shrink-0">
                  {settings.clinic_logo ? (
                    <div className="relative group">
                      <img 
                        src={settings.clinic_logo} 
                        alt="Clinic Logo" 
                        className="h-24 w-auto max-w-[200px] object-contain border rounded-lg p-2 bg-white"
                        data-testid="logo-preview"
                      />
                      <button
                        onClick={handleRemoveLogo}
                        className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid="remove-logo-btn"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="h-24 w-32 border-2 border-dashed border-muted-foreground/25 rounded-lg flex items-center justify-center bg-muted/50">
                      <ImageIcon className="w-8 h-8 text-muted-foreground/50" />
                    </div>
                  )}
                </div>

                {/* Upload Controls */}
                <div className="flex-1 space-y-3">
                  <div>
                    <p className="text-sm font-medium">Upload your clinic logo</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Recommended: Square or horizontal format. Max 500KB. PNG, JPG, or WEBP.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/webp"
                        onChange={handleLogoUpload}
                        className="hidden"
                        data-testid="logo-upload-input"
                      />
                      <Button asChild disabled={uploadingLogo}>
                        <span>
                          <Upload className="w-4 h-4 mr-2" />
                          {uploadingLogo ? 'Uploading...' : settings.clinic_logo ? 'Replace Logo' : 'Upload Logo'}
                        </span>
                      </Button>
                    </label>
                    {settings.clinic_logo && (
                      <Button variant="outline" onClick={handleRemoveLogo}>
                        <Trash className="w-4 h-4 mr-2" />
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Clinic Information Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Clinic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Clinic Name</Label>
                <Input
                  value={settings.clinic_name}
                  onChange={(e) => setSettings({ ...settings, clinic_name: e.target.value })}
                  placeholder="Your clinic name"
                  data-testid="clinic-name-input"
                />
              </div>
              <div>
                <Label>Address</Label>
                <Textarea
                  value={settings.clinic_address}
                  onChange={(e) => setSettings({ ...settings, clinic_address: e.target.value })}
                  placeholder="Full clinic address"
                  rows={2}
                />
              </div>
              <div>
                <Label>Phone Number</Label>
                <Input
                  value={settings.clinic_phone}
                  onChange={(e) => setSettings({ ...settings, clinic_phone: e.target.value })}
                  placeholder="+63 XXX XXX XXXX"
                />
              </div>
              <div>
                <Label>Edit Lock Period (hours)</Label>
                <Input
                  type="number"
                  value={settings.edit_lock_hours}
                  onChange={(e) => setSettings({ ...settings, edit_lock_hours: parseInt(e.target.value) || 24 })}
                  placeholder="24"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Cashiers cannot edit records older than this many hours
                </p>
              </div>
              <Button onClick={handleSaveSettings} data-testid="save-settings-btn">
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Staff Management Tab */}
        <TabsContent value="staff" className="mt-4 space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-medium">Staff Accounts</h3>
            <Button onClick={() => setShowUserForm(true)} data-testid="add-staff-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Staff
            </Button>
          </div>

          <div className="grid gap-3">
            {users.map((u) => (
              <Card key={u.user_id}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-medium">
                        {u.full_name?.charAt(0) || 'U'}
                      </div>
                      <div>
                        <p className="font-medium">{u.full_name}</p>
                        <p className="text-sm text-muted-foreground">{u.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getRoleBadgeColor(u.role)}`}>
                        {u.role}
                      </span>
                      {u.user_id !== currentUser?.user_id && (
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => setDeleteUserId(u.user_id)}
                        >
                          <Trash className="w-4 h-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Message Templates Tab */}
        <TabsContent value="templates" className="mt-4 space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-medium">Message Templates</h3>
            <Button onClick={() => {
              setEditingTemplate(null);
              setTemplateForm({ name: '', subject: '', body: '', template_type: 'follow_up' });
              setShowTemplateForm(true);
            }} data-testid="add-template-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Template
            </Button>
          </div>

          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-4">
              <p className="text-sm">
                <strong>Available placeholders:</strong><br />
                <code className="text-xs">{'{patient_name}'}</code> - Patient's full name<br />
                <code className="text-xs">{'{clinic_name}'}</code> - Your clinic name
              </p>
            </CardContent>
          </Card>

          {templates.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <p className="text-muted-foreground">No templates yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {templates.map((t) => (
                <Card key={t.template_id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">{t.name}</h4>
                          <span className="text-xs bg-secondary px-2 py-0.5 rounded capitalize">
                            {t.template_type.replace('_', ' ')}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          Subject: {t.subject}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                          {t.body}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => {
                            setEditingTemplate(t);
                            setTemplateForm({
                              name: t.name,
                              subject: t.subject,
                              body: t.body,
                              template_type: t.template_type
                            });
                            setShowTemplateForm(true);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDeleteTemplate(t.template_id)}
                        >
                          <Trash className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Staff Dialog */}
      <Dialog open={showUserForm} onOpenChange={setShowUserForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Staff Account</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Full Name *</Label>
              <Input
                value={userForm.full_name}
                onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })}
                placeholder="Staff member's full name"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                placeholder="email@clinic.com"
              />
            </div>
            <div>
              <Label>Password *</Label>
              <Input
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                placeholder="Temporary password"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={userForm.role} onValueChange={(v) => setUserForm({ ...userForm, role: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="nurse">Nurse / Staff</SelectItem>
                  <SelectItem value="cashier">Cashier / Accountant</SelectItem>
                  <SelectItem value="doctor">Doctor (Full Access)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowUserForm(false)}>Cancel</Button>
            <Button onClick={handleCreateUser} data-testid="save-staff-btn">Create Account</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Dialog */}
      <Dialog open={showTemplateForm} onOpenChange={setShowTemplateForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Edit Template' : 'Create Template'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Template Name *</Label>
              <Input
                value={templateForm.name}
                onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                placeholder="e.g., Follow-up Reminder"
              />
            </div>
            <div>
              <Label>Type</Label>
              <Select 
                value={templateForm.template_type} 
                onValueChange={(v) => setTemplateForm({ ...templateForm, template_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="follow_up">Follow-up</SelectItem>
                  <SelectItem value="reminder">Reminder</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Subject *</Label>
              <Input
                value={templateForm.subject}
                onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                placeholder="Email subject line"
              />
            </div>
            <div>
              <Label>Message Body *</Label>
              <Textarea
                value={templateForm.body}
                onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })}
                placeholder="Hi {patient_name}, this is {clinic_name}..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowTemplateForm(false)}>Cancel</Button>
            <Button onClick={handleSaveTemplate} data-testid="save-template-btn">
              {editingTemplate ? 'Save Changes' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation */}
      <AlertDialog open={!!deleteUserId} onOpenChange={() => setDeleteUserId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Staff Account?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this staff account. They will no longer be able to access the system.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUser} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SettingsPage;
